import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import {
  AgentRoutePricing,
  CreateAgentRoutePricingDto,
  UpdateAgentRoutePricingDto,
  BulkUpdateValidityDto
} from '../../models/forwarding/agent-route-pricing.model';

@Injectable({
  providedIn: 'root'
})
export class AgentRoutePricingService {
  private apiUrl = `${environment.apiUrl}/forwarding/agent-route-pricing`;
  private pricingsSubject = new BehaviorSubject<AgentRoutePricing[]>([]);
  public pricings$ = this.pricingsSubject.asObservable();

  constructor(private http: HttpClient) {}

  private loadPricings(): void {
    console.log('[AgentRoutePricingService] Loading agent route pricings from:', this.apiUrl);
    this.http.get<AgentRoutePricing[]>(this.apiUrl).subscribe({
      next: (pricings) => {
        console.log('[AgentRoutePricingService] Successfully loaded pricings from API:', pricings.length);
        this.pricingsSubject.next(pricings);
      },
      error: (error) => {
        console.error('[AgentRoutePricingService] Error loading pricings from API:', error);
        this.pricingsSubject.next([]);
      }
    });
  }

  getPricings(): Observable<AgentRoutePricing[]> {
    return this.pricings$;
  }

  getById(id: number): Observable<AgentRoutePricing> {
    return this.http.get<AgentRoutePricing>(`${this.apiUrl}/${id}`);
  }

  getByAgentRouteId(agentRouteId: number): Observable<AgentRoutePricing[]> {
    return this.http.get<AgentRoutePricing[]>(`${this.apiUrl}/by-agent-route/${agentRouteId}`);
  }

  create(dto: CreateAgentRoutePricingDto): Observable<AgentRoutePricing> {
    return this.http.post<AgentRoutePricing>(this.apiUrl, dto).pipe(
      tap(() => {
        console.log('[AgentRoutePricingService] Pricing created, reloading');
        this.loadPricings();
      })
    );
  }

  update(id: number, dto: UpdateAgentRoutePricingDto): Observable<AgentRoutePricing> {
    return this.http.put<AgentRoutePricing>(`${this.apiUrl}/${id}`, dto).pipe(
      tap(() => {
        console.log('[AgentRoutePricingService] Pricing updated, reloading');
        this.loadPricings();
      })
    );
  }

  bulkUpdateValidity(dto: BulkUpdateValidityDto): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/bulk-update-validity`, dto).pipe(
      tap(() => {
        console.log('[AgentRoutePricingService] Bulk validity updated, reloading');
        this.loadPricings();
      })
    );
  }

  delete(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${id}`).pipe(
      tap(() => {
        console.log('[AgentRoutePricingService] Pricing deleted, reloading');
        this.loadPricings();
      })
    );
  }

  refresh(): void {
    this.loadPricings();
  }
}
